/**
 * Color-mode conversion helpers for PSD import.
 *
 * All converters operate on raw 8-bit channel planes (as decoded from the PSD
 * channel image data) and produce a packed RGBA8 output buffer.
 *
 * PSD color-mode constants (from header.colorMode):
 *   0 = Bitmap, 1 = Grayscale, 2 = Indexed, 3 = RGB, 4 = CMYK,
 *   7 = Multichannel, 8 = Duotone, 9 = Lab
 *
 * Only the modes handled here produce a valid RGBA output; others should fall
 * back to the RGB path (or be treated as unsupported).
 */

// ---------------------------------------------------------------------------
// CMYK → RGB
// ---------------------------------------------------------------------------

/**
 * Convert a set of CMYK channel planes (as stored in a PSD file) to an RGBA8
 * buffer.
 *
 * **Critical PSD gotcha**: Photoshop stores CMYK channels *inverted*.
 * A stored byte value of 255 means "zero ink" (no ink applied) and a stored
 * value of 0 means "full ink".  So the actual ink fraction for a channel is
 * `(255 - stored) / 255`.
 *
 * Conversion formula (after de-inversion):
 *   C' = (255 - storedC) / 255   (actual Cyan fraction 0..1)
 *   M' = (255 - storedM) / 255
 *   Y' = (255 - storedY) / 255
 *   K' = (255 - storedK) / 255
 *   R  = round(255 × (1 - C') × (1 - K'))
 *   G  = round(255 × (1 - M') × (1 - K'))
 *   B  = round(255 × (1 - Y') × (1 - K'))
 *
 * @param cPlane   Raw stored C channel bytes (length = pixelCount).
 * @param mPlane   Raw stored M channel bytes.
 * @param yPlane   Raw stored Y channel bytes.
 * @param kPlane   Raw stored K channel bytes.
 * @param aPlane   Optional alpha channel bytes (channel id -1 in PSD).
 *                 When null/undefined the alpha component is set to 255.
 * @param pixelCount  Number of pixels (width × height).
 * @returns  Packed RGBA8 Uint8ClampedArray of length pixelCount * 4.
 */
export function cmykToRgba(
  cPlane: Uint8Array,
  mPlane: Uint8Array,
  yPlane: Uint8Array,
  kPlane: Uint8Array,
  aPlane: Uint8Array | null | undefined,
  pixelCount: number,
): Uint8ClampedArray {
  const rgba = new Uint8ClampedArray(pixelCount * 4);

  for (let i = 0; i < pixelCount; i++) {
    // De-invert: stored 255 = no ink = factor 0, stored 0 = full ink = factor 1.
    const c = (255 - (cPlane[i] ?? 0)) / 255;
    const m = (255 - (mPlane[i] ?? 0)) / 255;
    const y = (255 - (yPlane[i] ?? 0)) / 255;
    const k = (255 - (kPlane[i] ?? 0)) / 255;

    const base = i * 4;
    rgba[base + 0] = Math.round(255 * (1 - c) * (1 - k));
    rgba[base + 1] = Math.round(255 * (1 - m) * (1 - k));
    rgba[base + 2] = Math.round(255 * (1 - y) * (1 - k));
    rgba[base + 3] = aPlane != null ? (aPlane[i] ?? 255) : 255;
  }

  return rgba;
}

// ---------------------------------------------------------------------------
// Lab → RGB
// ---------------------------------------------------------------------------

/**
 * Convert a set of L*a*b* channel planes (as stored in a PSD file) to RGBA8.
 *
 * PSD encoding of Lab channels:
 *   - L channel: stored 0–255 maps linearly to L* 0–100.
 *   - a channel: stored 0–255 maps linearly to a* −128..+127.
 *   - b channel: stored 0–255 maps linearly to b* −128..+127.
 *
 * Conversion path: Lab → XYZ (D50 white point) → linear RGB → sRGB.
 *
 * The D50 white point is standard for Photoshop Lab documents.
 */
export function labToRgba(
  lPlane: Uint8Array,
  aPlane: Uint8Array,
  bPlane: Uint8Array,
  alphaPlane: Uint8Array | null | undefined,
  pixelCount: number,
): Uint8ClampedArray {
  const rgba = new Uint8ClampedArray(pixelCount * 4);

  for (let i = 0; i < pixelCount; i++) {
    // Decode stored bytes to L*, a*, b* values.
    const Lstar = ((lPlane[i] ?? 0) / 255) * 100;        // 0..100
    const aStar = ((aPlane[i] ?? 0) - 128);              // -128..127
    const bStar = ((bPlane[i] ?? 0) - 128);              // -128..127

    // Lab → XYZ (D50 white point: Xn=0.96422, Yn=1.00000, Zn=0.82521)
    const fy = (Lstar + 16) / 116;
    const fx = aStar / 500 + fy;
    const fz = fy - bStar / 200;

    const DELTA = 6 / 29;
    const DELTA3 = DELTA * DELTA * DELTA;
    const THREE_DELTA2 = 3 * DELTA * DELTA;

    const xr = fx > DELTA ? fx * fx * fx : THREE_DELTA2 * (fx - 4 / 29);
    const yr = fy > DELTA ? fy * fy * fy : THREE_DELTA2 * (fy - 4 / 29);
    const zr = fz > DELTA ? fz * fz * fz : THREE_DELTA2 * (fz - 4 / 29);

    // D50 white point (as used by Photoshop Lab)
    const X = xr * 0.96422;
    const Y = yr * 1.00000;
    const Z = zr * 0.82521;

    // XYZ → linear sRGB (using D50-adapted sRGB matrix — Bradford adaptation)
    // Matrix from ICC standard / ICC blog for D50 XYZ → sRGB:
    const rLin =  3.1338561 * X - 1.6168667 * Y - 0.4906146 * Z;
    const gLin = -0.9787684 * X + 1.9161415 * Y + 0.0334540 * Z;
    const bLin =  0.0719453 * X - 0.2289914 * Y + 1.4052427 * Z;

    // Linear → sRGB gamma (with clamping)
    const base = i * 4;
    rgba[base + 0] = linearToSrgb8(rLin);
    rgba[base + 1] = linearToSrgb8(gLin);
    rgba[base + 2] = linearToSrgb8(bLin);
    rgba[base + 3] = alphaPlane != null ? (alphaPlane[i] ?? 255) : 255;
  }

  return rgba;
}

/** Apply sRGB gamma and clamp to 0–255. */
function linearToSrgb8(linear: number): number {
  // Clamp first.
  const v = linear <= 0 ? 0 : linear >= 1 ? 1 : linear;
  const gamma =
    v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
  return Math.round(gamma * 255);
}

// ---------------------------------------------------------------------------
// Grayscale → RGBA
// ---------------------------------------------------------------------------

/**
 * Convert a single grayscale channel plane to RGBA8 by replicating G into R,
 * G, and B.
 *
 * @param grayPlane  Raw gray channel bytes (length = pixelCount).
 * @param alphaPlane Optional alpha channel bytes (channel id -1).
 * @param pixelCount Number of pixels.
 */
export function grayscaleToRgba(
  grayPlane: Uint8Array,
  alphaPlane: Uint8Array | null | undefined,
  pixelCount: number,
): Uint8ClampedArray {
  const rgba = new Uint8ClampedArray(pixelCount * 4);
  for (let i = 0; i < pixelCount; i++) {
    const g = grayPlane[i] ?? 0;
    const base = i * 4;
    rgba[base + 0] = g;
    rgba[base + 1] = g;
    rgba[base + 2] = g;
    rgba[base + 3] = alphaPlane != null ? (alphaPlane[i] ?? 255) : 255;
  }
  return rgba;
}

// ---------------------------------------------------------------------------
// Indexed → RGBA
// ---------------------------------------------------------------------------

/**
 * Convert indexed-color channel data to RGBA8 by looking up each byte in the
 * palette stored in the PSD's colorModeData section.
 *
 * The PSD color mode data for indexed-color documents is a 768-byte block:
 * 256 red values, then 256 green values, then 256 blue values (each 0–255).
 *
 * @param indexPlane  Raw index channel bytes (length = pixelCount).
 * @param colorModeData  The `PsdFile.colorModeData` Uint8Array (must be 768 bytes).
 * @param pixelCount  Number of pixels.
 */
export function indexedToRgba(
  indexPlane: Uint8Array,
  colorModeData: Uint8Array,
  pixelCount: number,
): Uint8ClampedArray {
  const rgba = new Uint8ClampedArray(pixelCount * 4);
  for (let i = 0; i < pixelCount; i++) {
    const idx = indexPlane[i] ?? 0;
    const base = i * 4;
    rgba[base + 0] = colorModeData[idx] ?? 0;           // R
    rgba[base + 1] = colorModeData[256 + idx] ?? 0;     // G
    rgba[base + 2] = colorModeData[512 + idx] ?? 0;     // B
    rgba[base + 3] = 255;                                // always opaque
  }
  return rgba;
}
